import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredArticles = useMemo(() => {
    if (!slug || slug === 'all') return articles;
    return articles.filter((a) => a.categorySlug === slug);
  }, [slug]);

  const sortedArticles = useMemo(() => {
    return [...filteredArticles].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [filteredArticles]);

  const categoryTitle = currentCategory.slug === 'all' ? '全部文章' : currentCategory.name;
  const pageTitle = `${categoryTitle} - 淫母日记 | 成人日记文学在线阅读`;
  const pageDesc = `浏览淫母日记${categoryTitle}分类下的所有成人日记文学作品。每日更新，免费在线阅读高质量原创内容。`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`淫母日记,${categoryTitle},成人日记,日记文学,在线阅读`}
        canonicalUrl={`https://elinggong.cn/category/${currentCategory.slug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: pageTitle,
          description: pageDesc,
          url: `https://elinggong.cn/category/${currentCategory.slug}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: categoryTitle },
                ]}
              />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-50">
                {categoryTitle}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                共 {sortedArticles.length} 篇文章
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-label transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    currentCategory.slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {sortedArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-search-line text-2xl text-foreground-500"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-300 mb-2">
                  暂无文章
                </h3>
                <p className="text-sm text-foreground-500 mb-6">
                  该分类下暂时没有文章，请浏览其他分类
                </p>
                <Link
                  to="/category/all"
                  className="px-5 py-2 rounded-full bg-primary-500 text-background-50 font-label text-sm hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
                >
                  <i className="ri-arrow-left-line"></i>
                  浏览全部文章
                </Link>
              </div>
            )}
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}